import { Component, inject, Input, OnInit } from '@angular/core';
import { DataLayer } from '../../../models/data-layer';
import { ButtonModule } from 'primeng/button';
import { GlobalsStore } from '../../../stores/globals.state';
import { NgClass } from '@angular/common';
import { ContextMenuModule } from 'primeng/contextmenu';
import { MenuItem, MenuItemCommandEvent } from 'primeng/api';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { PermissionService } from '../../../services/permission.service';
import { checkVisibleMenuItems } from '../../../utility/array.utility';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { Indicator } from '../../../models/indicator';
import { IndicatorDataStore } from '../../../stores/indicatpr-data/indicator-data.state';
import { IndicatorData } from '../../../models/indicator-data';

@Component({
  selector: 'app-year-button',
  standalone: true,
  imports: [ButtonModule, NgClass, ContextMenuModule],
  templateUrl: './year-button.component.html',
  styleUrl: './year-button.component.scss',
})
export class YearButtonComponent implements OnInit {
  dataStore = inject(IndicatorDataStore);
  globalStore = inject(GlobalsStore);
  dataLayerStore = inject(DataLayerStore);
  contextMenuItems: MenuItem[] | undefined;
  dataItem?: IndicatorData = undefined;

  @Input() dataLayer!: DataLayer;
  @Input() disabled = false;
  @Input() dataIsPublished: null | boolean = null;

  constructor(
    private permissionService: PermissionService,
    private confirmationDialogService: ConfirmationDialogService
  ) {}

  /**
   * Initializes the component after Angular has finished setting up the component's data-bound properties.
   * This method is automatically called after the component's construction is complete.
   * It is used here to invoke the setItems method which prepares necessary data or state for the component.
   *
   * @return {void} No return value.
   */
  ngOnInit(): void {
    this.setItems();
  }

  /**
   * Configures and sets the context menu items for a data layer based on
   * the current state and permissions.
   *
   * This method initializes a list of menu items with visibility, labels,
   * icons, and corresponding commands. The items are then processed to ensure
   * only visible items are assigned to the context menu.
   *
   * @return {void} No return value. The method updates the `contextMenuItems` property.
   */
  setItems(): void {
    const indicator = this.globalStore.selectedIndicator();
    if (this.dataIsPublished !== null && indicator) {
      this.dataItem = this.dataStore.getByDataLayerAndIndicator(
        this.dataLayer._id,
        indicator._id
      );
    }

    const items = [
      {
        visible: this.lockOrPublished()
          ? this.permissionService.checkDataLayerRights(
              this.dataLayer.parent,
              'lock'
            )
          : this.permissionService.checkDataLayerRights(
              this.dataLayer.parent,
              'unlock'
            ),
        label: this.lockOrPublished() ? 'intern schalten' : 'veröffentlichen',
        icon: this.lockOrPublished()
          ? 'fas fa-house-user'
          : 'fas fa-earth-europe',
        command: (event: MenuItemCommandEvent) => {
          const indicator = this.globalStore.selectedIndicator();
          this.dataLayerStore.singleSelection(this.dataLayer._id);
          setTimeout(() => {
            this.confirmationDialogService.confirmDataLayerPublication(
              undefined,
              this.dataLayer,
              () => {
                if (this.dataItem && indicator) {
                  console.log('stuff todo');
                  this.dataItem.published = !this.dataItem.published;
                  this.dataStore.update(this.dataItem);
                } else {
                  this.dataLayer.published = !this.dataLayer.published;
                  this.dataLayerStore.update(this.dataLayer);
                }
              }
            );
          });
        },
      },
      {
        visible: this.permissionService.checkDataLayerRights(
          this.dataLayer.parent,
          'remove'
        ),
        label: 'löschen',
        icon: 'fas fa-trash',
        command: (event: MenuItemCommandEvent) => {
          this.dataLayerStore.singleSelection(this.dataLayer._id);
          setTimeout(() => {
            this.confirmationDialogService.confirmDataLayerDeletion(
              undefined,
              this.dataLayer,
              () => this.dataLayerStore.deleteItem(this.dataLayer)
            );
          });
        },
      },
    ];

    this.contextMenuItems = checkVisibleMenuItems(items);
  }

  lockOrPublished() {
    return (
      (this.dataItem && this.dataItem.published) ||
      (!this.dataItem && this.dataLayer.published)
    );
  }

  /**
   * Updates the global store to set the currently selected data layer.
   * This method assigns the value of the data layer to a global state,
   * ensuring consistent data layer selection throughout the application.
   *
   * @return {void} Does not return a value.
   */
  selectDataLayer(): void {
    this.globalStore.setSelectedLayer(this.dataLayer);
  }

  /**
   * Determines the appropriate icon class based on the published state
   * of the data or the data layer.
   *
   * @return {'fas fa-earth-europe' | 'fas fa-lock'} Returns the icon class string depending on whether the data or
   * data layer is published. 'fas fa-earth-europe' indicates a public state,
   * while 'fas fa-lock' indicates a restricted or private state.
   */
  get iconByPublishedState(): 'fas fa-earth-europe' | 'fas fa-lock' {
    if (this.dataIsPublished !== null) {
      return this.dataIsPublished ? 'fas fa-earth-europe' : 'fas fa-lock';
    } else {
      return this.dataLayer.published ? 'fas fa-earth-europe' : 'fas fa-lock';
    }
  }
}
